import { memoize } from '@comeon/utility';
import Fuse from 'fuse.js';
import { createSelectorCreator } from 'reselect';
import {
	gameNamesByIdxSelector,
	providersSelector,
	subProvidersSelector,
	gamesByGameCodeSelector,
	gameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector,
	gamesByNameSelector
} from '~/components/casino-shared/ducks/selectors';
import type { CasinoSharedRootState } from '~/components/casino-shared/ducks/types';
import type { EnhancedGame, GameProvider } from '~/components/casino-shared/types';

const keywordSelector = (state: CasinoSharedRootState) => state.search.keyword;
const pageSelector = (state: CasinoSharedRootState) => state.search.page;
const scrollPositionSelector = (state: CasinoSharedRootState) => state.search.scrollPosition;
const graphyteSearchPendingSelector = (state: CasinoSharedRootState) => state.search.graphyte.searchPending;

export const searchResultsHash = (
	gameNamesByIdx: ReturnType<typeof gameNamesByIdxSelector>,
	_gamesByCode: ReturnType<typeof gamesByGameCodeSelector>,
	_gameCodesByProviderCode: ReturnType<typeof gameCodesByProviderCodeSelector>,
	_gameCodesBySubProviderCode: ReturnType<typeof gameCodesBySubProviderCodeSelector>,
	_normalizedProviderNamesSelector: string[],
	_normalizedSubProviderNamesSelector: string[],
	_gamesByName: ReturnType<typeof gamesByNameSelector>,
	keyword: string
) => {
	return `${gameNamesByIdx.length}-${keyword}`;
};

export const normalizeForSearch = (string: string) => string.replace(/[^a-zA-z]/gi, '').toUpperCase();

const providersHash = (providers: ReturnType<typeof providersSelector>) => {
	const providersLength = Object.keys(providers).length;

	return providersLength;
};

const providerSelectorCreator = createSelectorCreator(memoize, providersHash);

export const normalizedProviderNamesSelector = providerSelectorCreator([providersSelector], providers => {
	const providerNames = Object.values(providers).reduce<Record<string, string>>((acc, provider) => {
		acc[normalizeForSearch(provider.name)] = provider.name;
		return acc;
	}, {});
	return providerNames;
});

const searchSelectorCreator = createSelectorCreator(memoize, searchResultsHash);

export const searchOptions = {
	maxPatternLength: 10,
	shouldSort: true,
	distance: 100,
	threshold: 0.1
};

const searchResultSelector = searchSelectorCreator(
	[
		gameNamesByIdxSelector,
		gamesByGameCodeSelector,
		gameCodesByProviderCodeSelector,
		gameCodesBySubProviderCodeSelector,
		providersSelector,
		subProvidersSelector,
		gamesByNameSelector,
		keywordSelector
	],
	(
		gameNamesByIdx,
		gamesByCode,
		gameCodesByProviderCode,
		gameCodesBySubProviderCode,
		providers,
		subProviders,
		gamesByName,
		keyword
	) => {
		if (keyword) {
			const normalizedKeyword = normalizeForSearch(keyword);

			const normalizedProviders = Object.values(providers).reduce<Record<string, GameProvider>>(
				(acc, provider) => ({ ...acc, ...{ [normalizeForSearch(provider.name)]: provider } }),
				{}
			);

			if (Object.keys(normalizedProviders).includes(normalizedKeyword)) {
				// Show provider own games first and then subprovider games
				const providerOwnGames: EnhancedGame[] = [];
				const subProviderGames: EnhancedGame[] = [];

				gameCodesByProviderCode[normalizedProviders[normalizedKeyword].code].forEach(gameCode => {
					const game = gamesByCode[gameCode];
					if (game.casinoSubProviderVO) {
						subProviderGames.push(game);
					} else {
						providerOwnGames.push(game);
					}
				});
				return providerOwnGames.concat(subProviderGames);
			}

			const normalizedSubProviders = Object.values(subProviders).reduce<Record<string, GameProvider>>(
				(acc, subProvider) => ({ ...acc, ...{ [normalizeForSearch(subProvider.name)]: subProvider } }),
				{}
			);

			if (Object.keys(normalizedSubProviders).includes(normalizedKeyword)) {
				return gameCodesBySubProviderCode[normalizedSubProviders[normalizedKeyword].code].map(
					gameCode => gamesByCode[gameCode]
				);
			}
			const gameNameIndex = new Fuse(gameNamesByIdx, searchOptions);
			const searchNameIndexes = gameNameIndex.search(keyword).map(item => item.refIndex);
			const searchNames = searchNameIndexes.map(idx => gameNamesByIdx[idx]);
			return searchNames.map(gameName => gamesByName[gameName]);
		}
		return [];
	}
);

const graphyteResultsSelector = (state: CasinoSharedRootState) => {
	const { graphyte } = state.search;

	if (graphyte.searchSucceeded) {
		return graphyte.results;
	}

	// return false to indicate graphyte has failed
	// so that we can fallback to using local search
	return false;
};

const graphyteRecommendationSelector = (state: CasinoSharedRootState) => {
	const { graphyte } = state.search;

	if (graphyte.searchSucceeded) {
		return graphyte.recommendation;
	}

	return [];
};

const casinoExploreRecommendationSelector = (state: CasinoSharedRootState) =>
	state.search.graphyte.casinoExploreRecommendations;
const liveCasinoExploreRecommendationSelector = (state: CasinoSharedRootState) =>
	state.search.graphyte.liveCasinoExploreRecommendations;

export {
	casinoExploreRecommendationSelector,
	graphyteRecommendationSelector,
	graphyteResultsSelector,
	graphyteSearchPendingSelector,
	keywordSelector,
	liveCasinoExploreRecommendationSelector,
	pageSelector,
	scrollPositionSelector,
	searchResultSelector
};
